import type { ReactNode } from "react";
import type React from "react";
import { PureComponent } from "react";

import { connect } from "react-redux";

import type { IconName } from "@similarweb/icons";
import type { IPlainTooltipProps } from "@similarweb/ui-components";
import { Button, IconButton, PlainTooltip } from "@similarweb/ui-components";

import { showErrorToast } from "app/actions/toast_actions";
import { swSettings } from "app/common/services/swSettings";
import ContactUsButton from "app/components/ContactUs/ContactUsButton";
import ExcelClientDownload from "app/components/React/ExcelButton/ExcelClientDownload";
import { i18nFilter } from "app/filters/i18nFilter";
import { trackEvent as appcuesTrackEvent } from "app/services/AppcuesService";
import { openHooksModalV4 } from "app/services/ModalService";
import { allTrackers } from "app/services/track/track";
import { goToPackagesPage } from "app/UtilitiesAndConstants/UtilityFunctions/goToPackagesPage";
import LocationService from "pro-features/components/Modals/src/UnlockModal/LocationService";
import unlockModalConfig from "pro-features/components/Modals/src/UnlockModal/unlockModalConfig";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import UnlockModal from "../UnlockModalProvider/UnlockModalProvider";

import { PaidExcelDownloadControl } from "./PaidExcelDownloadControl";
import { DownloadButtonWrapper, ExportButtonsContainer, UpsellPopupText, UpsellPopupWrapper } from "./styledComponents";

import "./download-button-menu.scss";

const UpSellTooltipContent: React.FC<{
    upgradeText: string;
    unlockModalConfig?: object;
    upgradeButtonText: string;
    trackingEventName: string;
    onClick: VoidFunction;
}> = ({ upgradeText, unlockModalConfig, upgradeButtonText, trackingEventName, onClick }) => {
    return (
        <UpsellPopupWrapper>
            <UpsellPopupText data-automation="Excel Upgrade Text">{upgradeText}</UpsellPopupText>
            {unlockModalConfig ? (
                <Button
                    type="upsell"
                    onClick={() => {
                        // onClick(); // TODO: GADI to remove unused modal
                        goToPackagesPage();
                        allTrackers.trackEvent("pro hooks 2.0", "click", trackingEventName);
                    }}
                >
                    {upgradeButtonText}
                </Button>
            ) : (
                <ContactUsButton
                    label="Download Excel"
                    onClick={() => {
                        allTrackers.trackEvent(
                            "Upgrade Link",
                            "click",
                            `${LocationService.getCurrentLocationTracking()}/Download Excel`,
                        );
                    }}
                >
                    {upgradeButtonText}
                </ContactUsButton>
            )}
        </UpsellPopupWrapper>
    );
};

export interface IDownloadButtonMenuProps {
    Excel?: boolean;
    PaidExcel?: boolean;
    totalCount?: number;
    PNG?: boolean;
    excelLocked?: boolean;
    iconNameProps?: IconName;
    pngLocked?: boolean;
    exportFunction?: (type: string) => void;
    downloadUrl?: string;
    downloadTooltipText?: ReactNode;
    downloadTooltipConfig?: IPlainTooltipProps;
    excelBodyForReq?: object;
    errorToastString?: string;
    showToast?: (text?: string) => any;
    // An option to pass in the download logic to be run instead of the built in logic.
    // This should be used for PNG downloads.
    handleDownloadFunction?: (type?: string) => void | Promise<void>;
    buttonText?: string;
    isDisabled?: boolean;
    trackingFunction?: VoidFunction; // works currently for Excel/PNG
    showExcelEngagementPopup?: boolean;
}

interface IDownloadButtonMenuState {
    isUnlockModalOpen: boolean;
    isDownloading: boolean;
}

export class DownloadButtonMenu extends PureComponent<IDownloadButtonMenuProps, IDownloadButtonMenuState> {
    public unlockModalConfig;

    private swNavigator: any;

    private readonly upgradeText;

    private readonly upgradeButtonText;

    private readonly popupConfig;

    public static defaultProps = {
        isDisabled: false,
        buttonText: null,
    };

    constructor(props) {
        super(props);

        this.state = {
            isUnlockModalOpen: false,
            isDownloading: false,
        };

        this.swNavigator = proNavigator;
        this.unlockModalConfig = this.getUnlockModalConfig();

        this.upgradeText = i18nFilter()("directives.csv.notPermitted.text");
        this.upgradeButtonText = i18nFilter()("directives.csv.notPermitted.button");

        this.popupConfig = {
            cssClass: "Popup-element-wrapper--pro",
            cssClassContent: "Popup-content--pro",
            placement: "bottom",
            allowHover: true,
        };
    }

    public render() {
        return (
            <div className="export-buttons-wrapper">
                <ExportButtonsContainer>{this.getDownloadButton()}</ExportButtonsContainer>
                {this.unlockModal()}
            </div>
        );
    }

    private getDownloadButton() {
        const downloadButtons = [];
        if (this.props.PNG) {
            downloadButtons.push(this.getButtonWithPopup("PNG"));
        }
        if (this.props.Excel) {
            downloadButtons.push(this.getButtonWithPopup("Excel"));
        }
        return downloadButtons;
    }

    private defaultTracking = () => {
        const fileType = this.props.Excel ? "Excel" : "Png";
        return allTrackers.trackEvent("Download", "submit-ok", `Table/${fileType}`);
    };

    private handleDownload = async (type) => {
        const { trackingFunction = this.defaultTracking, showToast, errorToastString } = this.props;

        trackingFunction();
        this.setState({ isDownloading: true });
        let result: unknown | { success: boolean };
        try {
            result = this.props.handleDownloadFunction
                ? await this.props.handleDownloadFunction(type)
                : this.props.excelBodyForReq
                  ? await ExcelClientDownload(this.props.downloadUrl, this.props.excelBodyForReq)
                  : await ExcelClientDownload(this.props.downloadUrl);
        } catch (e) {
            if (this.props.errorToastString) {
                this.props.showToast?.(i18nFilter()(this.props.errorToastString));
            }
        } finally {
            if (result && Object.keys(result).length > 0 && result.hasOwnProperty("success")) {
                const { success } = result as { success: boolean };
                if (!success) {
                    showToast?.(i18nFilter()(errorToastString));
                }
            }
            this.setState({ isDownloading: false });
            if (this.props.exportFunction) {
                this.props?.exportFunction(type);
            }
        }
    };

    private unlockModal = () => {
        if (this.unlockModalConfig) {
            return (
                <UnlockModal
                    isOpen={this.state.isUnlockModalOpen}
                    onCloseClick={() => {
                        this.setState({ isUnlockModalOpen: false });
                    }}
                    {...this.unlockModalConfig}
                />
            );
        }
    };

    private upsellContent = () => {
        allTrackers.trackEvent(
            "pro hooks 2.0",
            "hover",
            `choose package/Excel/${LocationService.getCurrentLocationTracking()}`,
        );
        return (
            <UpSellTooltipContent
                trackingEventName={`choose package/Excel/${LocationService.getCurrentLocationTracking()}`} // CHTS-159
                upgradeButtonText={this.upgradeButtonText}
                upgradeText={this.upgradeText}
                unlockModalConfig={this.unlockModalConfig}
                onClick={() => this.setState({ isUnlockModalOpen: true })} // TODO: GADI to remove unused modal
            />
        );
    };

    private appcuseTrackButtonClick = (type) => {
        if (["Excel", "PNG"].includes(type)) {
            appcuesTrackEvent(`${type} Button Click`);
        }
    };

    private getButtonWithPopup(type: "Excel" | "PNG") {
        const {
            excelLocked,
            pngLocked,
            downloadUrl,
            downloadTooltipText,
            downloadTooltipConfig,
            isDisabled,
            buttonText,
            PaidExcel,
            iconNameProps,
            totalCount,
            excelBodyForReq,
            showExcelEngagementPopup,
            trackingFunction,
        } = this.props;
        let iconName: IconName = "download";
        if (iconNameProps) {
            iconName = iconNameProps;
        } else {
            if (type === "Excel") {
                iconName = excelLocked ? "excel-locked" : "excel";
            }
            if (type === "PNG" && pngLocked) {
                iconName = "download-locked";
            }
        }

        if (pngLocked || excelLocked) {
            return (
                <IconButton
                    iconSize={"sm"}
                    key={`single${type}`}
                    iconName="excel-download"
                    type="flatClickableDisabled"
                    onClick={async () => {
                        this.track(type);
                        const isSalesApp = this.swNavigator.isSalesSolution();
                        const hookName = isSalesApp ? "DownloadExcelSIRedirect" : "DownloadExcel";
                        await openHooksModalV4(hookName);
                    }}
                >
                    {buttonText ? i18nFilter()(buttonText) : null}
                </IconButton>
            );
        }

        if (PaidExcel) {
            return (
                <PaidExcelDownloadControl
                    type={type}
                    downloadUrl={downloadUrl}
                    totalCount={totalCount}
                    excelBodyForReq={excelBodyForReq}
                    downloadTooltipText={downloadTooltipText}
                    downloadTooltipConfig={downloadTooltipConfig}
                    buttonText={buttonText}
                    iconName={iconName}
                    isDisabled={isDisabled}
                    showExcelEngagementPopup={showExcelEngagementPopup}
                    trackingFunction={trackingFunction}
                />
            );
        }

        const hasUnlimitedExcelRows = swSettings.components?.Home?.resources?.DaaSExcelRowToDCRatioOverwite === 0;
        const isButtonDisabled = hasUnlimitedExcelRows ? isDisabled : false;

        return (
            <PlainTooltip
                key={`tooltip-download-${type.toLowerCase()}`}
                tooltipContent={downloadTooltipText ? downloadTooltipText : `${i18nFilter()("btn.download")} ${type}`}
                cssClass="PlainTooltip-element PlainTooltip--downloadButton"
                {...downloadTooltipConfig}
            >
                <DownloadButtonWrapper
                    className="DownloadButtonWrapper"
                    data-automation={`Download ${type}`}
                    data-downloadurl={downloadUrl}
                >
                    <IconButton
                        key={`single${type}`}
                        type="flat"
                        iconName={iconName}
                        onClick={() => {
                            this.appcuseTrackButtonClick(type);
                            this.handleDownload(type);
                        }}
                        isLoading={this.state.isDownloading}
                        isDisabled={isButtonDisabled}
                    >
                        {buttonText ? i18nFilter()(buttonText) : null}
                    </IconButton>
                </DownloadButtonWrapper>
            </PlainTooltip>
        );
    }

    private track(type) {
        this.appcuseTrackButtonClick(type);
        allTrackers.dispatchTrackEvent(
            "Download",
            "click",
            `${LocationService.getCurrentLocationTracking() || ""}/locked excel`,
        );
    }

    private getUnlockModalConfig() {
        return this.props.Excel && this.props.excelLocked
            ? {
                  location: `${LocationService.getCurrentLocationTracking()}/Download Table`,
                  ...unlockModalConfig().DownloadTable,
              }
            : null;
    }
}

const mapDispatchToProps = (dispatch) => ({
    showToast: (text?: string) => dispatch(showErrorToast(text)),
});

type OwnProps = Omit<IDownloadButtonMenuProps, "showToast">;

const connected = connect(null, mapDispatchToProps)(DownloadButtonMenu);

export default connected as unknown as React.ComponentType<OwnProps>;
